import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Popover,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListSendOutCollectionQuery } from "store/services/sendOutCollectionApi";
import { useAppMeta, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const SendOutCollectionListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Send Out Collections" }];

  const sendOutCollectionTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.sendOutCollectionId,
      cell: (row) => {
        const displayId = row?.sendOutCollectionId;
        const path = `/app/crm/sendOutCollection/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },

    {
      name: "Shipment",
      selector: (row) => row.shipmentrequest,
      cell: (row) => {
        const formatShipmentId = (shipmentId) =>
          shipmentId.replace(/(.{4})/g, "$1-").slice(0, -1);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [anchorEl, setAnchorEl] = React.useState(null);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedShipments, setSelectedShipments] = React.useState([]);

        const shipments = Array.isArray(row.shipmentrequest)
          ? row.shipmentrequest
          : [];
        const visibleShipments = shipments.slice(0, 2);
        const remainingCount = shipments.length - visibleShipments.length;

        const handleChipClick = (event, allShipments) => {
          setAnchorEl(event.currentTarget);
          setSelectedShipments(allShipments);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const open = Boolean(anchorEl);

        return shipments.length > 0 ? (
          <>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
              {visibleShipments.map((shipment, index) => (
                <span key={index} style={{ fontSize: "0.9rem" }}>
                  {formatShipmentId(shipment.shipmentId)}
                  {index !== visibleShipments.length - 1 && ","}
                </span>
              ))}

              {remainingCount > 0 && (
                <Tooltip title="Click to view all Shipment IDs">
                  <Chip
                    label={`+${remainingCount}`}
                    size="small"
                    sx={{
                      marginLeft: "4px",
                      height: "18px",
                      fontSize: "10px",
                      backgroundColor: "#e0e0e0",
                      color: "#333",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleChipClick(e, shipments)}
                  />
                </Tooltip>
              )}
            </Box>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box p={2} minWidth={100}>
                {selectedShipments.map((shipment, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {formatShipmentId(shipment.shipmentId)}
                  </Typography>
                ))}
              </Box>
            </Popover>
          </>
        ) : (
          "N/A"
        );
      },
    },

    {
      name: "Created By",
      selector: (row) => row.createdBy?.[0]?.name || "N/A",
      cell: (row) => <div>{row.createdBy?.[0]?.name || "N/A"}</div>,
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/sendOutCollection/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Collection"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/sendOutCollection/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Collection">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchSendOutCollections,
    { data: sendOutCollectionRes, isLoading, isFetching, error },
  ] = useLazyListSendOutCollectionQuery();
  console.log(sendOutCollectionRes, "sendOutCollectionRes");

  React.useEffect(() => {
    fetchSendOutCollections({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchSendOutCollections, keyword, perPage]);

  useAppMeta(`${activeTab} Send Out Collections`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={sendOutCollectionTabs} activeTab={activeTab} />
        <TableFilter
          searchLabel="Collection ID/Shipment ID"
          disabled={isLoading || isFetching}
        ></TableFilter>
        <AppTable
          columns={columns}
          data={sendOutCollectionRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={sendOutCollectionRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default SendOutCollectionListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/sendOutCollection/create">
      <Button startIcon={<Add />} variant="contained" color="primary">
        Add Collection
      </Button>
    </Link>
  );
};
